import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormHelperText,
  Grid2 as Grid,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useCallback, useRef } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import { DateTime } from "luxon";

const ScheduleRONComponent = ({
  handleImmediateRON,
  schedule,
  scheduledDate,
  setScheduledDate,
  setSchedule,
  isScheduled,
  ronDate,
  fiveMinBeforeSession,
  setOpen,
  sessionUrl,
  handleStartClick,
  cancelRef,
  handleTimeUp,
  handleTimeUpCalledRef,
  acceptanceInProgress = false,
  userTimezone,
}) => {
  const expandableRef = useRef(null);
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const largeTabletSize = useMediaQuery((theme) =>
    theme.breakpoints.down("lg"),
  );

  const startInstantCheck = async () => {
    await handleImmediateRON(); //! DO THIS
    setSchedule((prev) => ({
      ...prev,
      expeditedSelectedBtn: true,
      furtherSelectedBtn: false,
    }));
    setTimeout(() => {
      expandableRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 300); // Adjust time based on your collapse animation time
  };

  const handleDateChange = (newDate) => {
    setSchedule((prev) => ({
      ...prev,
      tempDate: newDate,
    }));
  };

  const handleTimeChange = (newTime) => {
    setSchedule((prev) => ({
      ...prev,
      tempTime: newTime,
    }));
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const handleFurtherClick = async () => {
    if (!schedule?.furtherSelectedBtn) {
      startFurtherCheck();
    }
  };
  const handleExpeditedClick = async () => {
    if (!schedule?.expeditedSelectedBtn) {
      startInstantCheck();
    }
  };

  const startFurtherCheck = async () => {
    setSchedule((prev) => ({
      ...prev,
      furtherSelectedBtn: true,
      expeditedSelectedBtn: false,
    }));
    setTimeout(() => {
      expandableRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 300); // Adjust time based on your collapse animation time
  };

  const formatSlotToUSDateTime = useCallback((slot) => {
    const date = new Date(slot.year, slot.month - 1, slot.day_of_month); // JavaScript months are 0-indexed
    const options = { month: "long", day: "numeric", year: "numeric" };
    const formattedDate = date.toLocaleDateString("en-US", options);

    // Convert 'HH:MM' format to 12-hour format with AM/PM
    const [hours, minutes] = slot.time.split(":");
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight
    const amPm = hours < 12 ? "AM" : "PM";
    const formattedTime = `${hours12}:${minutes} ${amPm}`;

    return `${formattedDate}, ${formattedTime}`;
  }, []);

  const formatAppointmentDate = useCallback(
    (dateObj, fallbackDateString) => {
      if (
        !dateObj ||
        !dateObj.year ||
        !dateObj.month ||
        !dateObj.day_of_month
      ) {
        return fallbackDateString || "";
      }

      const [hours, minutes] = (dateObj.time || "00:00")
        .split(":")
        .map((value) => parseInt(value, 10));

      const safeZone =
        userTimezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        "America/New_York";

      return DateTime.fromObject(
        {
          year: dateObj.year,
          month: dateObj.month,
          day: dateObj.day_of_month,
          hour: Number.isNaN(hours) ? 0 : hours,
          minute: Number.isNaN(minutes) ? 0 : minutes,
        },
        { zone: safeZone },
      ).toFormat("cccc, LLLL dd, yyyy hh:mm a");
    },
    [userTimezone],
  );

  const convertObjectToDayjs = useCallback(
    (dateInfo) => {
      // Day.js month is zero-indexed, subtract 1
      const monthZeroIndexed = dateInfo.month - 1;

      // Constructing the full date string
      const dateString = `${dateInfo.year}-${String(
        monthZeroIndexed + 1,
      ).padStart(2, "0")}-${String(dateInfo.day_of_month).padStart(2, "0")}`;

      // Constructing the time string
      const timeString = dateInfo.time;

      // Creating Day.js objects for date and time
      const date = dayjs(dateString);
      const time = dayjs(timeString, "HH:mm");

      // Setting the states
      setSchedule((prev) => ({
        ...prev,
        tempDate: date,
        tempTime: time,
      }));
    },
    [setSchedule],
  );

  return (
    <Box>
      <Box sx={{ mb: 2, px: 0.5 }}>
        <Typography variant="h6" align="center" color="primary">
          {!isScheduled &&
            "Would you like to check if a notary is available to assist you now?"}
        </Typography>

        {!isScheduled && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 1,
              mb: 5,
            }}
          >
            <Button
              size="small"
              disabled={
                acceptanceInProgress ||
                schedule?.isInstantAvailable ||
                schedule?.checkInstantLoading ||
                schedule?.expeditedSelectedBtn
              }
              onClick={handleFurtherClick}
              fullWidth={tabletSize}
              sx={{ minWidth: "20%" }}
              variant={schedule?.furtherSelectedBtn ? "contained" : "outlined"}
            >
              NO
            </Button>
            <LoadingButton
              size="small"
              disabled={acceptanceInProgress || schedule?.furtherSelectedBtn}
              loading={schedule?.yesLoading}
              onClick={handleExpeditedClick}
              fullWidth={tabletSize}
              sx={{ minWidth: "20%" }}
              variant={
                schedule?.expeditedSelectedBtn ? "contained" : "outlined"
              }
            >
              YES
            </LoadingButton>
          </Box>
        )}
      </Box>
      {/* EXPEDITED APPOINTMENT */}
      {isScheduled && (
        <>
          <Typography
            color="primary"
            align="center"
            variant="h6"
            px={2}
            sx={{ fontStyle: "italic" }}
          >
            You can easily join sessions directly from the Notarization tab,
            making it simple to manage your notarization tasks and stay up to
            date
          </Typography>
          <Box mb={3}>
            <Typography variant="h3" mt={13} align="center" color="primary">
              The session will begin at:
            </Typography>
            <Typography variant="h4" align="center" sx={{ color: "#4caf50" }}>
              {ronDate}
            </Typography>
          </Box>
          {fiveMinBeforeSession && (
            <Typography
              color="primary"
              variant="body1"
              fontWeight="bold"
              align="center"
            >
              You may join the session up to 5 minutes before the scheduled
              start time.
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <Button
              disabled={!fiveMinBeforeSession}
              color="success"
              size="large"
              component="a"
              href={sessionUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                setOpen(false);
              }}
              variant="contained"
              sx={{ minWidth: "25rem" }}
            >
              Join Session
            </Button>
          </Box>
        </>
      )}
      <Collapse in={schedule?.expeditedSelectedBtn}>
        <div ref={expandableRef}>
          <Grid container spacing={mobileSize ? 1 : 2} sx={{ mb: 2 }}>
            {schedule?.expeditedSelectedBtn && (
              <>
                <Grid
                  size={{ sm: 12, lg: 6 }}
                  sx={{ mx: mobileSize ? "0px" : "auto" }}
                >
                  <Card elevation={mobileSize ? 0 : 4}>
                    {schedule?.startInstantTimer &&
                      schedule?.instantAccepted === false && (
                        <Box
                          sx={{
                            backgroundColor: "#FF9800",
                            borderRadius: "20px 20px 0 0",
                            p: 1,
                            mt: mobileSize ? 2 : 0,
                          }}
                        >
                          <Typography
                            align="center"
                            color="#fff"
                            fontWeight="bold"
                            variant="body2"
                          >
                            {schedule?.immediateRequested === true
                              ? `Maximum waiting time left ${formatTime(
                                  schedule?.instantTimeLeft,
                                )}`
                              : `Selected Date And Time Is Reserved For: ${formatTime(
                                  schedule?.instantTimeLeft,
                                )}`}
                          </Typography>
                        </Box>
                      )}
                    <CardContent sx={{ pt: mobileSize ? 0 : 2, px: 0 }}>
                      <Typography
                        variant={mobileSize ? "h6" : "h5"}
                        align="center"
                        sx={{
                          mb: mobileSize ? 1 : 1,
                          mt: mobileSize ? 3 : 0,
                        }}
                      >
                        {schedule?.isInstantAvailable
                          ? "Expedited Notarization"
                          : "Expedited Appointments"}
                      </Typography>
                      {schedule?.immediateRequested === true &&
                        schedule?.startInstantTimer === true &&
                        schedule?.instantAccepted === false && (
                          <>
                            <Stack
                              direction="row"
                              justifyContent="center"
                              sx={{ width: "100%", fontWeight: "bold" }}
                            >
                              <Typography
                                align="center"
                                variant="tbody2"
                                sx={{ mb: 4, mr: 1, color: "#10b981" }}
                              >
                                Please wait while a notary is being assigned to
                                your expedited notarization request. Note:
                                Notaries have up to 5 minutes to accept your
                                request.
                              </Typography>
                            </Stack>
                          </>
                        )}
                      {schedule?.instantAccepted === true && (
                        <>
                          <Stack
                            direction="row"
                            justifyContent="center"
                            sx={{ width: "100%", fontWeight: "bold" }}
                          >
                            <Typography
                              align="center"
                              variant="tbody2"
                              sx={{ mb: 4, mr: 1, color: "#10b981" }}
                            >
                              Please wait while the system validates and
                              prepares your document for notarization. This
                              could take up to 60 seconds.
                            </Typography>
                          </Stack>
                        </>
                      )}

                      {schedule?.instantData?.success && (
                        <>
                          <Typography px={1} align="center">
                            We currently have{" "}
                            <span
                              style={{
                                fontWeight: "bold",
                                fontSize: "24px",
                                color: "#4caf50",
                              }}
                            >
                              {schedule?.instantData?.success
                                ? schedule?.instantData?.notaries.length
                                : 0}
                            </span>{" "}
                            {schedule?.instantData?.notaries.length > 1
                              ? "notaries"
                              : "notary"}{" "}
                            that might be available to assist you at:
                          </Typography>
                          {schedule?.isInstantAvailable && (
                            <Typography
                              sx={{ fontWeight: "bold" }}
                              align="center"
                            >
                              {formatAppointmentDate(
                                schedule?.instantData?.dateObj,
                                schedule?.instantData?.dateString,
                              )}
                            </Typography>
                          )}
                          {schedule?.immediateRequested === true &&
                          schedule?.startInstantTimer === true ? (
                            <></>
                          ) : (
                            <>
                              <Typography align="center" fontWeight="bold">
                                Would you like to submit a request for an
                                appointment?
                              </Typography>
                              <Typography align="center">
                                Please note that availability is not guaranteed.
                              </Typography>
                            </>
                          )}
                        </>
                      )}

                      <Grid
                        sx={{ width: "100%" }}
                        container
                        spacing={mobileSize ? 0 : 1}
                        pl={mobileSize ? 0.5 : tabletSize ? 1 : 2}
                        pr={mobileSize ? 0.5 : tabletSize ? 1 : 2}
                      >
                        <Grid
                          sx={{ order: mobileSize ? 2 : 1 }}
                          size={{
                            sm:
                              !schedule?.isInstantAvailable &&
                              schedule?.instantError
                                ? 12
                                : 6,
                            xs: 12,
                          }}
                        >
                          <Button
                            onClick={async () => {
                              // cancelRef.current = false;

                              setSchedule((prev) => ({
                                ...prev,
                                timeUpTriggered: false,
                              }));

                              if (
                                !schedule?.isInstantAvailable &&
                                schedule?.instantError
                              ) {
                                setSchedule((prev) => ({
                                  ...prev,
                                  expeditedSelectedBtn: false,
                                  furtherSelectedBtn: true,
                                  instantError: "",
                                }));
                                setTimeout(() => {
                                  expandableRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                  });
                                }, 300);
                              } else if (!schedule?.isInstantAvailable) {
                                setSchedule((prev) => ({
                                  ...prev,
                                  expeditedSelectedBtn: false,
                                }));
                              } else {
                                setSchedule((prev) => ({
                                  ...prev,
                                  instantTimeLeft: 0,
                                  canceledInstant: true,
                                  instantData: {},
                                  isInstantAvailable: false,
                                  expeditedSelectedBtn: false,
                                  immediateRequested: false,
                                  firstTimer: false,
                                }));
                              }
                              handleTimeUpCalledRef.current = false;
                              await handleTimeUp("instant");

                              // Lock first to prevent loop
                            }}
                            disabled={
                              Boolean(schedule?.checkInstantLoading) ||
                              Boolean(schedule?.instantAccepted) ||
                              Boolean(schedule?.immediateLock)
                            }
                            fullWidth
                            sx={{ mt: mobileSize ? 1 : 2 }}
                            variant="contained"
                            color={
                              !schedule?.isInstantAvailable &&
                              schedule?.instantError
                                ? "success"
                                : "error"
                            }
                          >
                            {!schedule?.isInstantAvailable &&
                            schedule?.instantError
                              ? "Schedule for a later time"
                              : "Cancel"}
                          </Button>
                        </Grid>
                        {!schedule?.isInstantAvailable &&
                        schedule?.instantError ? (
                          <></>
                        ) : (
                          <Grid
                            sx={{ order: mobileSize ? 1 : 2 }}
                            size={{ sm: 6, xs: 12 }}
                          >
                            <LoadingButton
                              size="small"
                              type={
                                schedule?.isInstantAvailable
                                  ? "submit"
                                  : "button"
                              }
                              loading={
                                schedule?.checkInstantLoading ||
                                schedule?.immediateRequested ||
                                schedule?.instantAccepted
                              }
                              onClick={() => {
                                if (!schedule?.isInstantAvailable) {
                                  startInstantCheck();
                                  setSchedule((prev) => ({
                                    ...prev,
                                    instantError: "",
                                  }));
                                } else if (schedule?.isInstantAvailable) {
                                  setSchedule((prev) => ({
                                    ...prev,
                                    submitting: true,
                                  }));
                                  handleTimeUpCalledRef.current = false;
                                  handleImmediateRON();
                                }
                              }}
                              disabled={
                                acceptanceInProgress ||
                                Boolean(scheduledDate) ||
                                Boolean(schedule?.slots.length !== 0) ||
                                Boolean(schedule?.checkDateLoading)
                              }
                              fullWidth
                              variant="contained"
                              sx={{ mt: 1.8 }}
                              color={
                                schedule?.isInstantAvailable
                                  ? "success"
                                  : "warning"
                              }
                              startIcon={
                                schedule?.isInstantAvailable &&
                                schedule?.immediateRequested === false &&
                                schedule?.checkInstantLoading === false ? (
                                  <InputAdornment position="start">
                                    <CheckCircleIcon
                                      style={{ color: "white" }}
                                    />
                                  </InputAdornment>
                                ) : null
                              }
                            >
                              {schedule?.isInstantAvailable
                                ? "Send Request"
                                : "Check Availability"}
                            </LoadingButton>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )}
          </Grid>
        </div>
      </Collapse>

      {/* FURTHER APPOINTMENT */}
      <Collapse in={schedule?.furtherSelectedBtn}>
        <div ref={expandableRef}>
          <Grid container spacing={mobileSize ? 1 : 2} sx={{ mb: 2 }}>
            <Grid
              size={{ sm: 12, lg: 6 }}
              sx={{ mx: mobileSize ? "0px" : "auto" }}
            >
              <Card elevation={mobileSize ? 0 : 4}>
                {schedule?.startTimer &&
                  schedule?.instantAccepted === false && (
                    <Box
                      sx={{
                        backgroundColor: "#FF9800",
                        borderRadius: "20px 20px 0 0",
                        p: 1,
                        mt: mobileSize ? 2 : 0,
                        mb: !schedule?.isManage && mobileSize ? 2 : undefined,
                      }}
                    >
                      <Typography
                        align="center"
                        color="#fff"
                        fontWeight="bold"
                        variant="body2"
                      >
                        {schedule?.furtherRequested === true
                          ? `Maximum waiting time left ${formatTime(
                              schedule?.scheduleTimeLeft,
                            )}`
                          : `Selected Date And Time Is Reserved For: ${formatTime(
                              schedule?.scheduleTimeLeft,
                            )}`}
                      </Typography>
                    </Box>
                  )}
                <CardContent
                  sx={{ pt: mobileSize ? 0 : 2, px: tabletSize ? 1 : 2 }}
                >
                  <Typography
                    variant={mobileSize ? "h6" : "h5"}
                    align="center"
                    sx={{
                      mb: schedule?.isAvailable || mobileSize ? 2 : 3,
                    }}
                  >
                    Schedule a later appointment date and time
                  </Typography>
                  {schedule?.furtherRequested === true &&
                    schedule?.startTimer === true &&
                    schedule?.instantAccepted === false && (
                      <>
                        <Stack
                          direction="row"
                          justifyContent="center"
                          sx={{ width: "100%", fontWeight: "bold" }}
                        >
                          <Typography
                            align="center"
                            variant="h6"
                            sx={{
                              mb: 0,
                              mr: 1,
                              mt: mobileSize ? 2 : undefined,
                              color: "#10b981",
                            }}
                          >
                            Please wait while a notary is being assigned to your
                            notarization request. Note: Notaries have up to 5
                            minutes to accept your request.
                          </Typography>
                        </Stack>
                      </>
                    )}
                  {schedule?.instantAccepted === true && (
                    <>
                      <Stack
                        direction="row"
                        justifyContent="center"
                        sx={{ width: "100%", fontWeight: "bold" }}
                      >
                        <Typography
                          align="center"
                          variant="tbody2"
                          sx={{ mb: 4, mr: 1, color: "#10b981" }}
                        >
                          Please wait while the system validates and prepares
                          your document for notarization. This could take up to
                          60 seconds.
                        </Typography>
                      </Stack>
                    </>
                  )}

                  {schedule?.furtherData?.success && (
                    <>
                      <Typography px={1} align="center">
                        We currently have{" "}
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "24px",
                            color: "#4caf50",
                          }}
                        >
                          {schedule?.furtherData?.success
                            ? schedule?.furtherData?.notaries.length
                            : 0}
                        </span>{" "}
                        {schedule?.furtherData?.notaries.length > 1
                          ? "notaries"
                          : "notary"}{" "}
                        that might be available to assist you.
                      </Typography>
                      {schedule?.isInstantAvailable && (
                        <Typography sx={{ fontWeight: "bold" }} align="center">
                          {formatAppointmentDate(
                            schedule?.furtherData?.dateObj,
                            schedule?.furtherData?.dateString,
                          )}
                        </Typography>
                      )}
                      {schedule?.furtherRequested === true &&
                      schedule?.startTimer === true ? (
                        <></>
                      ) : (
                        <Typography pb={1} align="center" fontWeight="bold">
                          Would you like to check if a notary is available to
                          assist you on your selected date and time?
                        </Typography>
                      )}
                    </>
                  )}

                  <Typography align="center" color="error">
                    {schedule?.furtherError}
                  </Typography>

                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <DatePicker
                      label="Select Date"
                      value={schedule?.tempDate}
                      onChange={handleDateChange}
                      // renderInput={(params) => <TextField {...params} />}
                      // slotProps={{textField: {...params}}}
                      disabled={
                        acceptanceInProgress ||
                        Boolean(schedule?.isInstantAvailable) ||
                        Boolean(schedule?.checkInstantLoading) ||
                        Boolean(schedule?.immediateRequested) ||
                        Boolean(schedule?.furtherData?.success)
                      }
                      disablePast
                    />
                    <TimePicker
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                      }}
                      closeOnSelect={false}
                      label="Select Time"
                      sx={{
                        borderRadius: "10px",
                      }}
                      value={schedule?.tempTime}
                      onChange={handleTimeChange}
                      // renderInput={(params) => <TextField {...params} />}
                      disabled={
                        acceptanceInProgress ||
                        Boolean(schedule?.furtherData?.success) ||
                        Boolean(schedule?.isInstantAvailable) ||
                        Boolean(schedule?.checkInstantLoading) ||
                        Boolean(schedule?.immediateRequested) ||
                        !schedule?.tempDate
                      }
                      minutesStep={5}
                    />
                  </Box>

                  <FormHelperText
                    sx={{ textAlign: "center", color: "error.main" }}
                  >
                    {schedule?.unavailableText}
                  </FormHelperText>
                  <Grid container spacing={1}>
                    <Grid
                      sx={{ order: mobileSize ? 2 : 1 }}
                      size={{ sm: 6, xs: 12 }}
                    >
                      <Button
                        onClick={async () => {
                          // Lock first to prevent loop
                          setSchedule((prev) => ({
                            ...prev,
                            furtherTimeUpTriggered: true,
                          }));

                          if (schedule?.startTimer) {
                            setSchedule((prev) => ({
                              ...prev,
                              scheduleTimeLeft: 0,
                            }));
                          }

                          setScheduledDate(null);
                          setSchedule((prev) => ({
                            ...prev,
                            tempDate: null,
                            tempTime: null,
                            slots: [],
                            isAvailable: false,
                            furtherSelectedBtn: false,
                            furtherDateObj: null,
                            unavailableText: "",
                            furtherData: null,
                            checkDateLoading: false,
                            furtherRequested: false,
                            isFurtherAvailable: false,
                            instantAccepted: false,
                            submitting: false,
                            startTimer: false,
                            furtherTimeUpTriggered: false,
                          }));
                          // Now safe to call without loop
                          cancelRef.current = false;
                          handleTimeUp("further");
                        }}
                        disabled={
                          acceptanceInProgress ||
                          Boolean(schedule?.instantAccepted)
                        }
                        fullWidth
                        sx={{ mt: mobileSize ? 0 : 2 }}
                        variant="contained"
                        color="error"
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid
                      sx={{ order: mobileSize ? 1 : 2 }}
                      size={{ sm: 6, xs: 12 }}
                    >
                      <LoadingButton
                        type={
                          schedule?.isInstantAvailable ? "submit" : "button"
                        }
                        loading={
                          schedule?.checkDateLoading ||
                          schedule?.furtherRequested ||
                          schedule?.instantAccepted
                        }
                        onClick={async () => {
                          if (!schedule?.isFurtherAvailable) {
                            await handleStartClick();
                            setSchedule((prev) => ({
                              ...prev,
                              slotIndex: -1,
                            }));

                            setTimeout(() => {
                              expandableRef.current?.scrollIntoView({
                                behavior: "smooth",
                              });
                            }, 300);
                          } else if (schedule?.isFurtherAvailable) {
                            setSchedule((prev) => ({
                              ...prev,
                              submitting: true,
                            }));
                            cancelRef.current = false;

                            handleStartClick();
                            // formik.submitForm(); //! RUN FUNCTION
                          }

                          // startFurtherCheck();
                        }}
                        disabled={
                          acceptanceInProgress ||
                          Boolean(!schedule?.furtherDateObj) ||
                          Boolean(!schedule?.tempDate) ||
                          Boolean(!schedule?.tempTime) ||
                          Boolean(schedule?.isAvailable) ||
                          Boolean(schedule?.checkInstantLoading)
                        }
                        fullWidth
                        size="small"
                        variant="contained"
                        sx={{ mt: mobileSize ? 1 : 2 }}
                        color={
                          schedule?.isFurtherAvailable ? "success" : "warning"
                        }
                        startIcon={
                          schedule?.isFurtherAvailable &&
                          schedule?.furtherRequested === false &&
                          schedule?.checkInstantLoading === false ? (
                            <InputAdornment position="start">
                              <CheckCircleIcon style={{ color: "white" }} />
                            </InputAdornment>
                          ) : null
                        }
                      >
                        {schedule?.isFurtherAvailable
                          ? "Send Request"
                          : "Check Availability"}
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </div>
      </Collapse>

      <Grid container spacing={largeTabletSize ? 1 : 2} px={1} align="center">
        {schedule?.slots.length !== 0 && (
          <Grid size={{ sm: 12 }} sx={{ mt: 2 }}>
            <Typography variant="h6" color="primary">
              Optimal Scheduling Options
            </Typography>
          </Grid>
        )}

        {schedule?.slots?.map((slot, index) => (
          <Grid size={{ md: 4, sm: 6 }} key={index}>
            <Button
              onClick={() => {
                if (schedule?.slotIndex === index) {
                  setSchedule((prev) => ({
                    ...prev,
                    tempDate: null,
                    tempTime: null,
                    furtherDateObj: null,
                    slotIndex: -1,
                  }));
                } else {
                  convertObjectToDayjs(slot);

                  setSchedule((prev) => ({
                    ...prev,
                    slotIndex: index,
                    unavailableText: "",
                  }));
                }
              }}
              fullWidth
              variant={schedule?.slotIndex === index ? "contained" : "outlined"}
            >
              {formatSlotToUSDateTime(slot)}
            </Button>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ScheduleRONComponent;
